/**
 * TextInput — labelled input field with consistent styling, inline
 * error message, and slots for optional icons / actions.
 *
 * Used by Login, Register, and most future forms. The wrapper styles
 * (rounded border, focus ring) live here so every form input across
 * ORIN looks identical without copy-pasting Tailwind classes.
 *
 * Props:
 *   label        — required label text
 *   id           — input id (also used for label htmlFor)
 *   type         — defaults to 'text'
 *   value, onChange, name, placeholder, autoComplete — standard input props
 *   required, disabled, readOnly — standard
 *   error        — string; renders red border + message below
 *   hint         — string; renders muted helper text below (hidden if error)
 *   leftIcon     — optional <Icon /> rendered inside the field on the left
 *   rightAction  — optional ReactNode rendered inside on the right
 *                  (e.g. the show/hide password toggle button)
 *   inputRef     — optional ref forwarded to the underlying input
 */

export default function TextInput({
  label,
  id,
  type = 'text',
  value,
  onChange,
  name,
  placeholder,
  autoComplete,
  required = false,
  disabled = false,
  readOnly = false,
  error,
  hint,
  leftIcon,
  rightAction,
  inputRef,
  ...rest
}) {
  const hasError = Boolean(error);
  const describedBy = hasError ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink mb-1.5">
        {label}
        {required && <span className="text-status-overdue ml-0.5" aria-hidden="true">*</span>}
      </label>

      <div
        className={[
          'flex items-center gap-2 px-3 py-2 rounded-md border bg-card transition-shadow',
          hasError ? 'border-status-overdue' : 'border-border-base',
          disabled || readOnly ? 'opacity-60' : '',
          'focus-within:ring-2 focus-within:ring-offset-0',
          hasError
            ? 'focus-within:border-status-overdue focus-within:ring-red-100'
            : '',
        ].join(' ')}
        style={
          !hasError
            ? {
                // Apply accent-colored focus ring via style so it respects the
                // user's chosen accent color (set via --accent CSS var)
              }
            : undefined
        }
      >
        {leftIcon && (
          <span className="text-muted flex-shrink-0" aria-hidden="true">
            {leftIcon}
          </span>
        )}
        <input
          ref={inputRef}
          id={id}
          name={name || id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-ink placeholder:text-slate-400 disabled:cursor-not-allowed"
          {...rest}
        />
        {rightAction && <span className="flex-shrink-0">{rightAction}</span>}
      </div>

      {hasError ? (
        <p
          id={`${id}-error`}
          className="mt-1.5 text-xs text-status-overdue"
          role="alert"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}